/**
 * InterimBubble
 *
 * Ghost bubble for live interim STT transcription during streaming mode.
 * Supports both doctor (right-aligned) and patient (left-aligned) directions.
 * Processing state shows "Translating..." with frozen text when batch STT is running.
 */

interface InterimBubbleProps {
  text: string;
  isFinal: boolean;
  direction?: 'doctor_to_patient' | 'patient_to_doctor';
  isProcessing?: boolean;
}

export function InterimBubble({ text, isFinal, direction = 'doctor_to_patient', isProcessing = false }: InterimBubbleProps) {
  const isDoctor = direction === 'doctor_to_patient';
  const speakerLabel = isDoctor ? 'Doctor' : 'Patient';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={isFinal ? `${speakerLabel} said: ${text}` : `Interim transcription: ${text}`}
      className={`flex flex-col gap-1 ${isDoctor ? 'items-end' : 'items-start'}`}
    >
      {/* Speaker label */}
      <div className={`flex items-center gap-1.5 ${isDoctor ? 'flex-row-reverse' : ''}`}>
        {isProcessing ? (
          <span className="flex items-center gap-1.5 text-xs font-body font-semibold text-amber-600 uppercase tracking-wide">
            <span
              className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0"
              aria-hidden="true"
            />
            Translating...
          </span>
        ) : isFinal ? (
          <span className="text-xs font-body font-semibold text-clinical-text-muted uppercase tracking-wide">
            {speakerLabel}
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-xs font-body font-semibold text-clinical-text-muted uppercase tracking-wide">
            <span
              className="w-1.5 h-1.5 rounded-full bg-clinical-teal animate-pulse flex-shrink-0"
              aria-hidden="true"
            />
            Listening...
          </span>
        )}
      </div>

      {/* Bubble */}
      <div
        className={[
          'max-w-[85%] px-4 py-3 rounded-clinical text-sm font-body leading-relaxed',
          'transition-all duration-300 ease-out',
          isDoctor
            ? `rounded-br-sm text-white ${
                isProcessing
                  ? 'bg-clinical-charcoal/70 opacity-80 border border-dashed border-amber-400/50'
                  : isFinal
                  ? 'bg-clinical-charcoal opacity-100 border border-solid shadow-clinical'
                  : 'bg-clinical-charcoal/70 opacity-70 border border-dashed border-clinical-charcoal/30 animate-ghostPulse'
              }`
            : `rounded-bl-sm text-clinical-charcoal-soft ${
                isProcessing
                  ? 'bg-white opacity-80 border border-dashed border-amber-400/50'
                  : isFinal
                  ? 'bg-white opacity-100 border border-clinical-border shadow-clinical'
                  : 'bg-white/70 opacity-70 border border-dashed border-clinical-border/50 animate-ghostPulse'
              }`,
        ].join(' ')}
      >
        <p>{text}</p>
      </div>
    </div>
  );
}
